import { useMutation, useQueryClient } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase';
import { PACK_FILES } from './packFiles';
import { categoriesKey, skillsKey } from './skillsQueries';

// The repository pack under "skills projects/" is split into four skills along
// the lines it already has on disk. Everything under skills/<name>/ is its own
// skill, because that is what Claude Code installs; the rules, prompts and
// agent travel together as one.
interface PackSkill {
  slug: string;
  name: string;
  description: string;
  /** Predicate over the pack-relative path. */
  match: (path: string) => boolean;
  /** Strip the prefix so the skill folder starts at its own root. */
  rewrite: (path: string) => string;
}

const PACK_SKILLS: PackSkill[] = [
  {
    slug: 'hebrew-house-rules',
    name: 'כללי הבית לעברית',
    description:
      'הליבה: כללים לשוניים, מיקרוקופי, RTL, אזורים אסורים, תבנית מונחון, שני הפרומפטים והסוכן.',
    match: (p) => !p.startsWith('skills/'),
    rewrite: (p) => p,
  },
  {
    slug: 'hebrew-content-writer',
    name: 'hebrew-content-writer',
    description: 'סקיל חיצוני של skills-il: משלב, דקדוק, דוגרי, Heblish, SEO.',
    match: (p) => p.startsWith('skills/hebrew-content-writer/'),
    rewrite: (p) => p.slice('skills/hebrew-content-writer/'.length),
  },
  {
    slug: 'hebrew-rtl-best-practices',
    name: 'hebrew-rtl-best-practices',
    description: 'סקיל חיצוני של skills-il: bidi, תכונות לוגיות ב-CSS, היפוך אייקונים.',
    match: (p) => p.startsWith('skills/hebrew-rtl-best-practices/'),
    rewrite: (p) => p.slice('skills/hebrew-rtl-best-practices/'.length),
  },
  {
    slug: 'israeli-ui-design-system',
    name: 'israeli-ui-design-system',
    description: 'סקיל חיצוני של skills-il: דשבורדים, טפסים, פורמטים ישראליים, טיפוגרפיה.',
    match: (p) => p.startsWith('skills/israeli-ui-design-system/'),
    rewrite: (p) => p.slice('skills/israeli-ui-design-system/'.length),
  },
];

export const REPO_PACK_SKILLS = PACK_SKILLS.length;

/**
 * One-way import: repo files become rows. Deliberately NOT a sync.
 * A two-way sync between a database and a git working tree is a conflict
 * engine, and the whole point of moving to the database is that the database
 * becomes the source of truth. Re-running this on an existing slug is refused
 * rather than merged.
 */
export function useImportRepoPack(orgId: string | null, userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<number> => {
      if (!orgId) throw new Error('אין ארגון פעיל');

      const { data: existing, error: existingError } = await supabase
        .from('skills')
        .select('slug')
        .eq('organization_id', orgId)
        .is('deleted_at', null);
      if (existingError) throw existingError;
      const taken = new Set(existing.map((r) => r.slug));

      const { data: category, error: categoryError } = await supabase
        .from('skill_categories')
        .insert({
          organization_id: orgId,
          slug: 'hebrew',
          name: 'עברית',
          description: 'כתיבה, לוקליזציה ו-RTL בעברית.',
          sort_order: 0,
        })
        .select('id')
        .single();
      if (categoryError) throw categoryError;
      const categoryId = category.id;

      let created = 0;
      for (const spec of PACK_SKILLS) {
        if (taken.has(spec.slug)) continue;
        const files = PACK_FILES.filter((f) => spec.match(f.path));
        if (files.length === 0) continue;

        const { data: skill, error: skillError } = await supabase
          .from('skills')
          .insert({
            organization_id: orgId,
            category_id: categoryId,
            slug: spec.slug,
            name: spec.name,
            description: spec.description,
            status: 'published',
            version: '1.0.0',
            tags: ['עברית'],
          })
          .select('id')
          .single();
        if (skillError) throw skillError;
        const skillId = skill.id;

        const rows = files.map((f, index) => ({
          organization_id: orgId,
          skill_id: skillId,
          path: spec.rewrite(f.path),
          content: f.body,
          sort_order: index,
        }));
        const { error: filesError } = await supabase.from('skill_files').insert(rows);
        if (filesError) throw filesError;

        const { error: versionError } = await supabase.from('skill_versions').insert({
          organization_id: orgId,
          skill_id: skillId,
          version: '1.0.0',
          note: 'ייבוא ראשוני מהריפו',
          snapshot: rows.map((r) => ({ path: r.path, content: r.content })),
          created_by: userId,
        });
        if (versionError) throw versionError;

        created += 1;
      }

      return created;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: categoriesKey(orgId) });
      await qc.invalidateQueries({ queryKey: skillsKey(orgId) });
    },
  });
}
